import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Paper, Grid, Typography, Button, Alert, AlertTitle, Stack, Chip, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import RefreshIcon from '@mui/icons-material/Refresh';
import LanIcon from '@mui/icons-material/Lan';
import LayersIcon from '@mui/icons-material/Layers';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RuleIcon from '@mui/icons-material/Rule';
import PublicIcon from '@mui/icons-material/Public';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import SpeedIcon from '@mui/icons-material/Speed';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { motion } from 'motion/react';
import {
  getIpamConfig, getIpamSummary, listIpamMembers,
  startIpamRefresh, getIpamActiveRefreshJob, getIpamRefreshJob,
} from '../../../../services/ipamApi';
import PortalPageHeader from '../../../../components/portal/PortalPageHeader';
import {
  PortalLoading, PortalErrorState, PortalEmptyState, PortalSectionHeader,
} from '../../../../components/portal/PortalStates';
import { pageVariants, sectionVariants } from '../../../../animations/variants';
import OperationProgress from '../OperationProgress';
import { POLL_INTERVAL_MS } from '../pollInterval';
import {
  CK, FONT_BODY, FONT_MONO, sectionCardSx, insetPanelSx, tableHeadRowSx, headCellSx,
  rowSx, bodyCellSx, cidrCellSx, statusChipSx, statTileSx, statTileFooterSx,
  tileEntranceStyle, UtilizationBar,
} from '../../portalStyles';
import IpamDiscoveryColdStart, { IPAM_DISCOVERY_POLL_MS } from './IpamDiscoveryColdStart';
import { IPAM_UTILIZATION_THRESHOLDS } from './ipamState';
import {
  IPAM_ADVANCED_PRICE, IPAM_PRICE_NOTE, formatMonthlyCostUsd,
} from './ipamPricing';
// IMPORTED, NEVER RETYPED. The caption under the meter names where the figure
// came from, and it named the wrong source for a fortnight — see ipamCopy.js.
import {
  IPAM_ACTIVE_IPS_TILE_LABEL, IPAM_ADDRESS_SPACE_VS_METER,
  IPAM_DASHBOARD_INVENTORY_UNREAD_BODY, IPAM_DASHBOARD_INVENTORY_UNREAD_TITLE,
  IPAM_METERED_SUBTITLE,
  IPAM_PUBLIC_IPS_TILE_UNREAD_CAPTION, IPAM_PUBLIC_IPS_TILE_UNREAD_HINT,
  ipamConfigNoteSeverity, ipamConfigNoteTitle, ipamCostChip, ipamDeleteItYourself,
  ipamMeteredCaption, ipamPreviewBillingLine, ipamTierChip,
} from './ipamCopy';

// The VPC IPAM dashboard.
//
// EVERY NUMBER ON THIS PAGE IS A ROLLUP, AND EVERY ROLLUP HERE HAS A WAY OF
// LYING. The four that this file exists to prevent:
//
//  1. COLD START IS NOT EMPTINESS. "Enabled, associations active, zero rows" is
//     the NORMAL first ~20 minutes of every tenant's life (AWS's discovery is a
//     periodic background sweep, not a live query). It is discriminated on the
//     SERVER-side config.firstDiscoveryAt, never on a client timer — a timer is
//     wrong across a reload, wrong for a tab left open overnight, and wrong for
//     an account enrolled six months later (DD-30).
//  2. SILENT BREAKAGE OUTRANKS EMPTINESS. A member whose resource discovery was
//     deleted, or whose RAM share was revoked, reports ResourceDiscoveryStatus
//     'not-found' and NOTHING ELSE ON ITS ROW CHANGES. Every table on this page
//     then silently omits that account while looking perfectly clean, so the
//     coverage gap is drawn as a warning ABOVE everything else
//     (OrphanScanPage.js:573-600), and the empty state beneath it is scoped to
//     the accounts that ARE discovering.
//  3. AN HONEST ZERO NAMES ITS SCOPE. The bare words "No resources found" are
//     never rendered here; the empty state names the accounts × Regions it
//     covers and when the sweep last ran, because that is the only thing that
//     makes an honest zero distinguishable from a broken pipeline.
//  4. ABSENT IS NOT ZERO. A rollup Prism could not read renders as an em-dash
//     with its reason, never as 0 — and a nil IpUsage means AWS cannot compute
//     utilization (it cannot for IPv6 at all), not "0% used".
//
// THE MANAGED/UNMANAGED TILE IS DELIBERATELY ABSENT — see the posture card
// below. ManagementState is eventually consistent with a multi-minute lag, so a
// freshly created resource reads 'unmanaged' while it is merely pending.

const IPAM_COLOR = '#455A64';
const IPAM_COLOR_DARK = '#31434B';

// Three consecutive background-poll failures stop the interval, matching
// AccessAnalyzerEntry.js:22. A transient blip must not surface an error card, and
// a permanently broken poll must not hammer the API forever.
const MAX_POLL_FAILURES = 3;

// ---------------------------------------------------------------------------
// GET /ipam/summary — the shape this page reads.
//
// The service method behind /summary is being written in a parallel lane, so
// this page is written against getIpamSummary() exactly like any other call and
// it lights up with no edit here. EVERY FIELD BELOW IS OPTIONAL AND EVERY ONE
// DEGRADES TO AN EM-DASH, never to 0 — a rollup that does not exist yet must
// not render as "your org is clean".
//
//   {
//     resourceCidrCount, vpcCount, publicAddressCount,     // whole inventory
//     activeIpCount,                                        // metered active IPs
//     compliance:  { compliant, noncompliant, unmanaged, ignored, unrecognized },
//     overlap:     { overlapping, nonoverlapping, ignored, unrecognized },
//     management:  { managed, unmanaged, pending, ignored, unrecognized },
//     topUtilization: [{ resourceId, resourceName, resourceCidr, resourceType,
//                        resourceOwnerId, resourceRegion, ipUsage }],
//     utilization: { measuredCidrs, unmeasurableCidrs, average },
//     accountsInScope, regionsInScope, lastDiscoveryRefreshAt   // config wins
//   }
//
// The three posture rollups are ALL vpc-rows-only, and EVERY ONE OF THEM CARRIES
// AN `unrecognized` BUCKET — services/ipam_service.go:16403-16445, whose own
// comment is the contract this page has to honour: "a blank status on a vpc row
// lands here and is not laundered… so the five fields always reconcile against
// VpcCount". A page that renders four of the five buckets throws away exactly the
// VPCs AWS has not assessed yet and leaves the reader with chips that add to less
// than the VPC count beside them, with nothing on screen saying so. Every bucket
// the server ships is drawn here, and the arithmetic is stated out loud.
//
// `ipUsage` is AWS's RAW FRACTION (0.375 = 37.5%), exactly as GetIpamResourceCidrs
// returns it and as models.IpamScopeCidr stores it — never a pre-scaled percent.
// `utilization` carries the ranking's DENOMINATORS: measuredCidrs is what the
// ranking covers and unmeasurableCidrs is what it silently does not, which is why
// the caption under the table prints it rather than just alluding to it.
// `management` ships ONLY when the server has applied the ManagementState
// staleness gate; absent, the page renders no management figure at all.
// ---------------------------------------------------------------------------

// countOrNull is the whole "absent is not zero" rule in four lines. Number(v||0)
// — the shape at AccessAnalyzerDashboard.js:140 — turns a missing rollup into a
// confident 0, which on THIS page reads as "no address-space conflicts anywhere
// in your organization".
const countOrNull = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const formatCount = (v) => (v === null ? null : v.toLocaleString());

// AWS reports IpUsage as a fraction; UtilizationBar takes 0..100. nil stays nil:
// it means "not computable" (IPv6 has no utilization at all), and folding it to
// 0 would paint every IPv6 prefix as an empty, healthy space.
const usagePercent = (ipUsage) => {
  if (ipUsage === null || ipUsage === undefined || ipUsage === '') return null;
  const n = Number(ipUsage);
  return Number.isFinite(n) ? n * 100 : null;
};

// relativeTime returns a short "2 min ago" style string for a past ISO time.
const relativeTime = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const diffSec = Math.max(0, (Date.now() - then) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.round(diffSec / 60)} min ago`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)} h ago`;
  return `${Math.round(diffSec / 86400)} d ago`;
};

// The empty state names WHEN the sweep ran, so an absolute stamp rather than
// "3 h ago" — "as of 3 h ago" is not a fact anyone can check against AWS.
const absoluteTime = (iso) => {
  if (!iso) return null;
  const t = new Date(iso);
  return Number.isNaN(t.getTime()) ? null : t.toLocaleString();
};

// IpamConfig.OperatingRegions is a JSON []string held in a text column, so it
// arrives as a STRING. An unparseable value yields null — "unknown" — and never
// an empty array, because "0 regions in scope" is a sentence this page would
// then print as fact.
const parseOperatingRegions = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string' && raw.trim() !== '') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : null;
    } catch (_) {
      return null;
    }
  }
  return null;
};

const ipamContainedSx = {
  backgroundColor: IPAM_COLOR,
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': { backgroundColor: IPAM_COLOR_DARK },
  '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
};

const captionSx = { fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted };

// StatTile is the one tile shape the stat row uses, so every tile is the same
// height and the row reads as one system.
//
// `value === null` is rendered as a muted em-dash carrying its reason, NOT as a
// zero. That is the single most important line in this component.
function StatTile({
  icon, value, color, label, caption, hint, onClick,
  // WHY the em-dash, when it is not "the rollup did not answer". A cache the
  // server has never read WHOLE answers 200 with a zero, and a zero on a tile is
  // a claim; the tile shows unknown instead and has to say which unknown it is.
  unavailableCaption = 'Not available — Prism could not read this rollup',
}) {
  const unavailable = value === null;
  const tile = (
    <Paper
      elevation={0}
      onClick={unavailable ? undefined : onClick}
      sx={{
        ...statTileSx,
        ...(onClick && !unavailable
          ? {
            cursor: 'pointer',
            transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
            '&:hover': { borderColor: color, boxShadow: `0 4px 12px ${color}20` },
          }
          : {}),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '10px',
            backgroundColor: unavailable ? `${CK.muted}12` : `${color}12`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: unavailable ? CK.muted : color,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Typography
          sx={{
            fontFamily: FONT_BODY,
            fontWeight: 700,
            fontSize: unavailable ? '2rem' : '2.5rem',
            color: unavailable ? CK.muted : color,
            lineHeight: 1,
          }}
        >
          {unavailable ? '—' : formatCount(value)}
        </Typography>
      </Box>
      <Box sx={statTileFooterSx}>
        <Typography
          sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: '1rem', color: CK.navy, mb: 0.5 }}
        >
          {label}
        </Typography>
        <Typography sx={{ ...captionSx, fontSize: '0.8rem', fontWeight: 500 }}>
          {unavailable ? unavailableCaption : caption}
        </Typography>
      </Box>
    </Paper>
  );
  return hint ? <Tooltip title={hint}>{tile}</Tooltip> : tile;
}

// The label Prism gives the `unrecognized` bucket. It is the ROLLUP of the state
// IpamAddressSpace.js already draws per-row as "not reported", and it is worded
// the same way on purpose: the same VPC must not be called one thing on the row
// and another in the summary.
const NOT_REPORTED_LABEL = 'Not reported';

const notReportedHint = (vocabulary) =>
  `VPCs AWS has not assessed for ${vocabulary} yet, plus any whose value names something `
  + "Prism's vocabulary does not know. AWS reports this on VPC rows, so a blank one is an "
  + 'anomaly rather than a known-inapplicable value — the server counts them here instead of '
  + 'dropping them, which is what makes these buckets add up to the VPC count. '
  + 'Not reported is not the same as clean.';

// notReportedBucket builds the `unrecognized` chip for one vocabulary.
//
// AMBER ONLY WHEN THERE IS SOMETHING TO SEE. A standing amber "Not reported: 0"
// is a false alarm on every healthy tenant; grey at zero is the honest reading of
// a bucket that is genuinely empty. Above zero it is emphatically not grey —
// grey is what `Ignored` wears, and `Ignored` is a decided, benign outcome
// (ipamState.js:36-52).
const notReportedBucket = (vocabulary, value) => ({
  key: 'unrecognized',
  label: NOT_REPORTED_LABEL,
  intent: value ? 'warning' : 'neutral',
  hint: notReportedHint(vocabulary),
  value,
});

// PostureCounts renders one vocabulary's breakdown as chips AND RECONCILES IT
// AGAINST THE VPC COUNT IN WRITING.
//
// Two rules, and the second is the one this component was missing:
//
//  1. A bucket the server did not send is omitted rather than drawn as zero —
//     the "absent is not zero" rule, same as every tile on this page.
//  2. EVERY BUCKET THE SERVER DID SEND IS DRAWN. Rendering four of the five the
//     Go rollup ships dropped the `unrecognized` count entirely, so a tenant with
//     40 VPCs saw a header chip reading "40 VPCs" beside chips adding to 35, with
//     no way to discover the difference was five VPCs AWS has not assessed. The
//     subtraction is now done on screen rather than left to the reader, and when
//     it does not come out the card says so instead of looking tidy.
//
// `total` is vpcCount, which is itself nullable — an unread denominator produces
// "cannot be reconciled", never a silent pass.
function PostureCounts({ buckets, total, unassessed = false, unassessedFloor = null }) {
  // THE FIFTH ARM, AND THE ONE THAT USED TO CERTIFY ITS OWN LIE. When AWS's
  // per-scope view has reported no VPC at all, every bucket the server ships is
  // a real, well-formed 0, so the reconciliation below found sum === total === 0,
  // declared itself reconciled, and printed "All 5 buckets shown; they account
  // for every one of the 0 discovered VPCs" in muted grey — an audit passing
  // itself over an estate nothing has looked at. Live-observed 2026-08-23 with 14
  // CIDRs sitting in ipam_discovered_cidrs. There is nothing to draw here: a
  // bucket that reads 0 because nobody was asked is not a bucket.
  if (unassessed) {
    return (
      <Typography sx={captionSx}>
        Not assessed yet — AWS has not reported a single VPC in the IPAM scope view, which is where
        all of these verdicts come from. Every bucket would read zero, and a zero here would be a
        pass nothing has earned.
      </Typography>
    );
  }

  const present = buckets.filter((b) => b.value !== null);
  if (present.length === 0) {
    return (
      <Typography sx={captionSx}>
        Not available — Prism could not read this rollup.
      </Typography>
    );
  }

  const unreadable = buckets.length - present.length;
  const sum = present.reduce((acc, b) => acc + b.value, 0);
  const gap = total === null ? null : total - sum;
  const reconciled = unreadable === 0 && gap === 0;

  let reconciliation;
  if (unreadable > 0) {
    reconciliation = `These buckets add to ${formatCount(sum)}, but Prism could not read `
      + `${unreadable} of the ${buckets.length} — so that is a floor, not a total, and it cannot `
      + `be reconciled against ${total === null ? 'the VPC count' : `the ${formatCount(total)} VPCs`}.`;
  } else if (total === null) {
    reconciliation = `All ${buckets.length} buckets add to ${formatCount(sum)}. Prism could not `
      + 'read the VPC count, so there is nothing here to reconcile them against.';
  } else if (gap === 0) {
    // "discovered VPCs" WAS THE WRONG NOUN and it made this line certify more than
    // it had checked: `total` is vpcCount, which counts the SCOPE table, so the
    // sentence claimed coverage of the whole estate while describing only the part
    // AWS had assessed. The remainder is named rather than left out.
    reconciliation = `All ${buckets.length} buckets shown; they account for every one of the `
      + `${formatCount(total)} VPCs AWS has assessed`
      + (unassessedFloor
        ? `. At least ${formatCount(unassessedFloor)} more discovered VPCs are not in the scope `
          + 'view yet and are in none of these buckets — they are unassessed, not clean.'
        : ' — every discovered VPC.');
  } else if (gap > 0) {
    reconciliation = `These buckets add to ${formatCount(sum)} of ${formatCount(total)} VPCs — `
      + `${formatCount(gap)} ${gap === 1 ? 'is' : 'are'} unaccounted for. Prism cannot say which `
      + 'bucket they belong in; treat them as unassessed rather than as clean.';
  } else {
    reconciliation = `These buckets add to ${formatCount(sum)}, more than the `
      + `${formatCount(total)} VPCs Prism counted. The two figures do not reconcile, so at least `
      + 'one of them is stale.';
  }

  return (
    <>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {present.map((b) => {
          const chip = (
            <Chip
              key={b.key}
              size="small"
              label={`${b.label}: ${formatCount(b.value)}`}
              sx={statusChipSx(b.intent)}
            />
          );
          return b.hint ? <Tooltip key={b.key} title={b.hint}>{chip}</Tooltip> : chip;
        })}
      </Stack>
      <Typography
        sx={{ ...captionSx, mt: 1.5, color: reconciled ? CK.muted : CK.amber }}
      >
        {reconciliation}
      </Typography>
    </>
  );
}

// residuePresent — does the caller already know about leftover teardown resources?
//
// IpamEntry owns the TEARDOWN-RESIDUE banner ("teardown left resources behind in
// N accounts, and the meter is still running"); this page owns the ENABLE
// partial-failure alert ("some accounts enrolled and others did not"). Both used
// to fire together, because residue is only ever fetched WHEN the status IS
// `partial_failure` — so the two banners stacked and the second one narrated a
// half-failed teardown as an enrolment problem. This page now steps aside when
// Entry has the more specific fact.
//
// Shape-tolerant on purpose: Entry holds residue as [accountId, detail] entry
// PAIRS, the wire carries it as a map, and the prop is absent entirely on the
// routes that mount this dashboard directly. All three must answer honestly, and
// "no residue" is the answer that lets THIS page's alert render — the default when
// nobody told us otherwise is the alert we already showed.
const residuePresent = (residue) => {
  if (Array.isArray(residue)) return residue.length > 0;
  if (residue && typeof residue === 'object') return Object.keys(residue).length > 0;
  return false;
};

export default function IpamDashboard({ config: configProp, residue }) {
  const navigate = useNavigate();

  // IpamEntry owns the config and re-polls it; the spec's render table shows
  // `<IpamDashboard />` without naming its props, so the prop is taken when it is
  // given and fetched when it is not. The page cannot fall back to "assume warm"
  // — firstDiscoveryAt IS the cold-start discriminator, and guessing it wrong in
  // either direction is one of the two failure modes this file exists to prevent.
  const [config, setConfig] = useState(configProp || null);
  const [summary, setSummary] = useState(null);
  const [members, setMembers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summaryError, setSummaryError] = useState(null);
  const [membersError, setMembersError] = useState(null);
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [refreshError, setRefreshError] = useState(null);
  const [lastCheckedAt, setLastCheckedAt] = useState(null);

  const jobPollRef = useRef(null);
  const discoveryPollRef = useRef(null);
  const pollFailuresRef = useRef(0);

  // ── THE PROP IS A SEED, NOT A SUBSCRIPTION ─────────────────────────────────
  //
  // `const configProvided = !!configProp;` → `if (!configProvided) await
  // loadConfig();` meant that on the ONE path this component is actually mounted
  // from — IpamEntry, which always passes `config` — NOTHING in this file ever
  // re-read GET /config. Not the header Refresh, not the refresh job completing,
  // not the 30 s cold-start poll. And IpamEntry only re-polls while the status is
  // `in_progress`, which is a state that routes AWAY from this component, so on
  // an `enabled` tenant every config-derived fact below was frozen for the life
  // of the mount:
  //
  //   coldStart              — the operator presses "Check for results", the
  //                            sweep lands, the server stamps firstDiscoveryAt,
  //                            and this panel goes on saying "AWS is still taking
  //                            its first inventory … nothing to retry" FOREVER,
  //                            with the CLIENT stamp ticking beside a frozen
  //                            SERVER stamp — which is the exact pairing that
  //                            made the falsehood convincing.
  //   lastSweepIso           — config wins over the fresher `summary`.
  //   inventoryCachesRead    — an unread cache never becomes read.
  //   publicAddressesRead    — same, for the bill-shaped zero.
  //   activeIpsCountedRel    — the age of the metered figure never moves.
  //
  // So the seed is taken once and every later pass re-reads it. The three sibling
  // pages already do this — their `checkNow` calls `loadContext()`, which re-reads
  // the config — and this is the same contract, not a new one.
  const configSeededRef = useRef(!!configProp);
  useEffect(() => {
    if (configProp) setConfig(configProp);
  }, [configProp]);

  const loadConfig = useCallback(async () => {
    const res = await getIpamConfig();
    setConfig(res?.data || null);
  }, []);

  // The summary's failure is NON-FATAL and is reported in place: the config,
  // enrollment health and billing panels are all still readable and still true
  // without it. What it must never do is leave zeros on screen — setSummary(null)
  // sends every tile through the em-dash path.
  const loadSummary = useCallback(async () => {
    try {
      const res = await getIpamSummary();
      setSummary(res?.data || null);
      setSummaryError(null);
    } catch (e) {
      setSummary(null);
      setSummaryError(e.message);
    }
  }, []);

  // Enrollment health is what State C is computed from, so a FAILED read here is
  // not "no accounts are broken" — it is "Prism cannot tell", and the discovery
  // panel says exactly that rather than rendering an all-clear.
  const loadMembers = useCallback(async () => {
    try {
      const res = await listIpamMembers();
      setMembers(Array.isArray(res?.data) ? res.data : []);
      setMembersError(null);
    } catch (e) {
      setMembers(null);
      setMembersError(e.message);
    }
  }, []);

  const loadAll = useCallback(async () => {
    // NOT A POLL STORM: this adds exactly one GET /config per pass that was
    // already happening — the mount, a refresh job settling, or one 30 s
    // cold-start tick. The mount's own pass is skipped when the host has just
    // handed the row down, so the seeded case costs nothing.
    if (configSeededRef.current) {
      configSeededRef.current = false;
    } else {
      await loadConfig();
    }
    await Promise.all([loadSummary(), loadMembers()]);
    setLastCheckedAt(new Date().toISOString());
  }, [loadConfig, loadSummary, loadMembers]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        await loadAll();
        if (!ignore) setError(null);
      } catch (e) {
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [loadAll]);

  // Re-attach to a refresh already running server-side (started before a
  // navigation away or a full page reload), so the progress bar survives both.
  useEffect(() => {
    let ignore = false;
    getIpamActiveRefreshJob()
      .then((res) => {
        const active = res?.data;
        if (!ignore && active && active.status === 'running') {
          setJob((prev) => prev || active);
        }
      })
      .catch(() => { /* transient — the refresh button still works */ });
    return () => { ignore = true; };
  }, []);

  const jobId = job?.id;
  const jobStatus = job?.status;

  // The JOB poll runs at the shared 3 s cadence and that is deliberate: this is
  // Prism's own fan-out with an exact UnitsTotal, not AWS's ~20-minute sweep, so
  // a bar polled every 30 s would look frozen. The DISCOVERY poll below is the
  // one that must not use it.
  //
  // Keyed on the two PRIMITIVES, never on `job` — an object dependency rebuilds
  // the interval on every progress tick, so the cadence drifts to
  // POLL_INTERVAL_MS + RTT and any failure counter resets each cycle.
  useEffect(() => {
    if (!jobId || jobStatus !== 'running') return undefined;
    jobPollRef.current = setInterval(async () => {
      try {
        const res = await getIpamRefreshJob(jobId);
        const next = res?.data;
        setJob(next);
        if (next && next.status !== 'running') {
          clearInterval(jobPollRef.current);
          jobPollRef.current = null;
          await loadAll();
        }
      } catch (e) {
        // getIpamRefreshJob rejects once a job ages out of the table, so this
        // loop stops rather than retrying against a row that will never return.
        // It says so and releases the button: a progress bar left spinning on a
        // job nobody is reading any more is the same lie as a frozen one.
        clearInterval(jobPollRef.current);
        jobPollRef.current = null;
        setJob(null);
        setRefreshError(`Lost track of the running refresh: ${e.message}`);
      }
    }, POLL_INTERVAL_MS);
    return () => {
      if (jobPollRef.current) {
        clearInterval(jobPollRef.current);
        jobPollRef.current = null;
      }
    };
  }, [jobId, jobStatus, loadAll]);

  // STATE A's discriminator. Server-side, not a client timer (DD-30).
  const coldStart = !!config && config.firstDiscoveryAt == null;

  // The COLD-START poll: IPAM_DISCOVERY_POLL_MS (30 s), never the shared 3 s
  // POLL_INTERVAL_MS. AWS's first sweep takes up to ~20 minutes, so 3 s here is
  // roughly 400 pointless requests per tenant per cold start — and none of them
  // can make the sweep finish sooner.
  //
  // Keyed on the `coldStart` primitive, interval owned by a ref, and three
  // consecutive failures stop it.
  useEffect(() => {
    if (!coldStart) return undefined;
    pollFailuresRef.current = 0;
    discoveryPollRef.current = setInterval(async () => {
      try {
        await loadAll();
        pollFailuresRef.current = 0;
      } catch (e) {
        pollFailuresRef.current += 1;
        if (pollFailuresRef.current >= MAX_POLL_FAILURES) {
          clearInterval(discoveryPollRef.current);
          discoveryPollRef.current = null;
          setError(e.message);
        }
      }
    }, IPAM_DISCOVERY_POLL_MS);
    return () => {
      if (discoveryPollRef.current) {
        clearInterval(discoveryPollRef.current);
        discoveryPollRef.current = null;
      }
    };
  }, [coldStart, loadAll]);

  const onRefresh = useCallback(async (kind) => {
    setStarting(true);
    setRefreshError(null);
    try {
      const res = await startIpamRefresh(kind);
      const started = res?.data || null;
      setJob(started);
      setLastCheckedAt(new Date().toISOString());
      // A job that is ALREADY terminal never enters the poll below, so nothing
      // would re-read the config for it — and a refresh that completed inline is
      // exactly the pass most likely to have stamped firstDiscoveryAt.
      if (!started || started.status !== 'running') await loadAll();
    } catch (e) {
      setRefreshError(e.message);
    } finally {
      setStarting(false);
    }
  }, [loadAll]);

  // --- Enrollment / discovery health -------------------------------------
  //
  // Built from GET /members, which answers today, rather than from the summary:
  // State C is the one thing on this page that must not wait on another lane.
  const discovery = useMemo(() => {
    if (!Array.isArray(members)) {
      return { known: false, associations: [], inScope: null, notFound: null, discovering: null };
    }
    // 'disabled' and 'removing' rows are deliberate departures, not breakage.
    const live = members.filter((m) => m.phase !== 'disabled' && m.phase !== 'removing');
    const notFound = live.filter((m) => m.resourceDiscoveryStatus === 'not-found');
    // The host row carries no resource discovery of its own — the IPAM's default
    // discovery covers it — so a chip for it would be permanently "unknown", i.e.
    // a standing false alarm in the one component whose job is to make a real
    // alarm visible.
    const associations = live
      .filter((m) => m.role !== 'host')
      .map((m) => ({
        accountId: m.accountId,
        region: m.discoveryRegion,
        status: m.resourceDiscoveryStatus,
      }));
    const accountIds = new Set(live.map((m) => m.accountId));
    const inScope = accountIds.size > 0 ? accountIds.size : null;
    return {
      known: true,
      associations,
      inScope,
      notFound: notFound.length,
      discovering: inScope === null ? null : inScope - notFound.length,
      notFoundAccounts: notFound.map((m) => m.accountId),
    };
  }, [members]);

  const operatingRegions = parseOperatingRegions(config?.operatingRegions);
  const regionCount = operatingRegions ? operatingRegions.length : null;
  const brokenDiscovery = discovery.known && discovery.notFound > 0;

  // "{n} accounts × {m} regions currently in scope" — and when discovery IS
  // broken, the scope is the accounts that are STILL DISCOVERING, never the full
  // enrollment. Scoping an honest zero to accounts Prism knows it cannot see is
  // how a coverage gap gets reported as a clean result.
  const scopePhrase = useMemo(() => {
    const accounts = brokenDiscovery ? discovery.discovering : discovery.inScope;
    if (accounts === null || accounts === undefined) {
      return 'the accounts and Regions currently in scope';
    }
    const noun = brokenDiscovery
      ? `${accounts} ${accounts === 1 ? 'account' : 'accounts'} still discovering`
      : `${accounts} ${accounts === 1 ? 'account' : 'accounts'}`;
    if (regionCount === null) return `the ${noun} currently in scope`;
    return `the ${noun} × ${regionCount} ${regionCount === 1 ? 'Region' : 'Regions'} currently in scope`;
  }, [brokenDiscovery, discovery.discovering, discovery.inScope, regionCount]);

  // --- Rollups -----------------------------------------------------------
  // TWO CIDR COUNTS, FROM TWO DIFFERENT AWS VIEWS, AND THEY GO OUT OF STEP.
  //
  // resourceCidrCount is ipam_scope_cidrs (GetIpamResourceCidrs, the per-scope
  // view) and is the denominator of every posture number below. discoveredCidrCount
  // is ipam_discovered_cidrs (GetIpamDiscoveredResourceCidrs, the per-discovery
  // view). AWS populates the first only AFTER the second has landed — verified
  // live on the POC tenant 2026-08-23, where both scopes returned zero resource
  // CIDRs while both discoveries returned seven each.
  //
  // The dashboard computed discoveredCidrCount and rendered it NOWHERE, so on a
  // freshly enabled tenant every tile, every rollup and the empty state below all
  // read a confident zero over 14 CIDRs AWS had reported and Prism had stored.
  const resourceCidrCount = countOrNull(summary?.resourceCidrCount);
  const discoveredCidrCount = countOrNull(summary?.discoveredCidrCount);
  const vpcCount = countOrNull(summary?.vpcCount);
  const discoveredVpcCount = countOrNull(summary?.discoveredVpcCount);
  const publicAddressCount = countOrNull(summary?.publicAddressCount);
  const overlapping = countOrNull(summary?.overlap?.overlapping);
  const noncompliant = countOrNull(summary?.compliance?.noncompliant);
  const topUtilization = Array.isArray(summary?.topUtilization) ? summary.topUtilization : [];

  // The ranking's DENOMINATORS. The caption under the table used to claim that
  // prefixes AWS cannot measure are "excluded from this ranking" without ever
  // printing how many — which is the same shape of omission as a missing bucket:
  // an admission with no magnitude attached. The server ships both numbers
  // (IpamUtilizationSummary) precisely "so the operator can see how much of the
  // estate the number does not cover".
  const measuredCidrs = countOrNull(summary?.utilization?.measuredCidrs);
  const unmeasurableCidrs = countOrNull(summary?.utilization?.unmeasurableCidrs);

  // The metered count: the summary's live figure when it exists, otherwise the
  // rolling estimate on the config. -1 IS THE "UNAVAILABLE" SENTINEL and 0 is a
  // real, different answer — collapsing them would render an unavoidable charge
  // as "$0.00 / month", the one rendering that makes a bill look free.
  const configActiveIps = countOrNull(config?.estimatedActiveIps);
  const activeIps = countOrNull(summary?.activeIpCount)
    ?? (configActiveIps !== null && configActiveIps >= 0 ? configActiveIps : null);
  const monthlyCost = formatMonthlyCostUsd(activeIps);

  // ── IS THE METER ACTUALLY RUNNING? ─────────────────────────────────────────
  //
  // Not `config.tier`. GET /summary now carries `stillBilling`, derived from the
  // LEDGER by the same evidence GetTeardownPreview uses, and `billingNote`, the
  // reason in prose. `tier` is a SETTING and `estimatedActiveIps` is a
  // timestamped MEASUREMENT; neither establishes that anything is being charged,
  // and the header chip was spending the first of them as though it did — over
  // an IPAM AWS had confirmed deleted, beside a dollar figure.
  //
  // THREE STATES. `stillBilling` is absent on an older payload and whenever the
  // ledger read degraded — the backend falls back to the status with a warning
  // rather than failing — so absent is "not established" and is hedged, never
  // spent as "no".
  const stillBilling = summary?.stillBilling;
  const billingNote = String(summary?.billingNote || '').trim();
  const ipamAdopted = config?.ipamAdopted === true;
  const tierChip = ipamTierChip({
    tier: config?.tier,
    stillBilling,
    adopted: ipamAdopted,
    // Straight through from the backend rather than re-derived from `status` here:
    // meterVerdict already decides when the claim is frozen, and a second spelling
    // of that rule in the client is how the chip and the caption drift apart.
    verified: summary?.billingVerified,
  });
  // Same flag the tier chip takes, for the same reason and from the same field.
  // Without it this chip is the ONE surface on the page that turns an unverified
  // reading into SUCCESS GREEN: the degraded path reaches `stillBilling === false`
  // off the config status alone, and on an adopted IPAM `disabled` is entirely
  // compatible with an Advanced-tier meter that is still running.
  const costChip = ipamCostChip({ monthlyCost, stillBilling, verified: summary?.billingVerified });
  // THE DIMMING RULE, AND IT IS NOT `stillBilling === false` ON ITS OWN. Greying
  // the dollar figure is this tile's way of saying "this money is historical" — a
  // settled fact — and an unverified stop has settled nothing. So the figure stays
  // in full navy unless the stop was actually verified; only the chip's wording
  // softens. Dimming it on an unverified read would restate the green all-clear in
  // typography after the chip had just stopped saying it.
  const meterStopped = stillBilling === false && summary?.billingVerified !== false;

  // WHEN THE METER WAS COUNTED — and it is NOT the sweep time above.
  //
  // Both wire fields are the same column: summary.activeIpCount is
  // ipam_configs.estimated_active_ips translated for JSON, and
  // config.estimatedActiveIpsAt dates it. It is written by estimateActiveIPs from
  // DescribeNetworkInterfaces, which for a long time ran ONLY at enable — the
  // figure was frozen on enable day and captioned "billing you for right now".
  // A full refresh re-counts it now, and the re-count is allowed to fail without
  // failing the refresh, so this age is load-bearing rather than decorative: it
  // is the only thing on the page that separates a figure counted a minute ago
  // from one nobody has managed to re-read in a fortnight.
  const activeIpsCountedRel = relativeTime(config?.estimatedActiveIpsAt);

  const lastSweepIso = config?.lastDiscoveryRefreshAt || summary?.lastDiscoveryRefreshAt || null;
  const lastSweepRel = relativeTime(lastSweepIso);
  const lastSweepAbs = absoluteTime(lastSweepIso);

  const refreshing = jobStatus === 'running';
  // An empty complete pass is a LEGITIMATE complete pass, so `degraded` is never
  // computed from rowsFetched === 0 — only from the structured degradation the
  // job itself carries (models/ipam_models.go, IpamRefreshJob).
  const degraded = jobStatus === 'completed'
    && ((job?.unitsFailed || 0) > 0 || (job?.unitsUnscanned || 0) > 0 || !!job?.note);

  // THE THIRD CASE, MADE ROOM FOR IN A NAME. Discovery has reported CIDRs and the
  // per-scope view has reported none of them: nothing on this page that is scoped
  // to ipam_scope_cidrs may be presented as a measurement, and none of it may be
  // presented as a clean one. Both halves must be KNOWN — a null count is unread,
  // and an unread count never routes anywhere.
  const scopeViewBehind = discoveredCidrCount !== null && discoveredCidrCount > 0
    && resourceCidrCount === 0;

  // AND THE PARTIAL WINDOW, which `resourceCidrCount === 0` does not cover and
  // which is the ordinary case rather than the exotic one: enrol an account after
  // the initial enable and its discovered VPCs land while every existing
  // account's scope rows are already there. resourceCidrCount is then > 0, the
  // notice above stays silent, and the rollups quietly denominate themselves in
  // the VPCs that HAVE been assessed while the new account's are invisible to
  // every number on the page.
  //
  // A FLOOR, NOT A COUNT. The two AWS views need not cover identical row sets, so
  // the gap is stated as "at least N" and is never subtracted into a denominator.
  const unassessedVpcFloor = (discoveredVpcCount !== null && vpcCount !== null
    && discoveredVpcCount > vpcCount)
    ? discoveredVpcCount - vpcCount
    : null;
  const someVpcsUnassessed = unassessedVpcFloor !== null && unassessedVpcFloor > 0;

  const postureChipLabel = (() => {
    if (scopeViewBehind) return 'Not assessed yet';
    if (vpcCount === null) return 'VPC-scoped';
    if (someVpcsUnassessed) return `${formatCount(vpcCount)} of ${formatCount(discoveredVpcCount)} VPCs assessed`;
    return `${formatCount(vpcCount)} VPCs`;
  })();

  // STATE B. Requires ALL THREE counts to be a known, real zero: an unread rollup
  // is null here, and null must never route to "AWS found nothing". The third one
  // is the fix — without discoveredCidrCount this state fired over a tenant whose
  // discovery had already reported 14 CIDRs, and said so with a timestamp.
  const inventoryLooksEmpty = !coldStart && resourceCidrCount === 0
    && publicAddressCount === 0 && discoveredCidrCount === 0;

  // AND THREE ZEROS ARE STILL NOT A SWEEP. Every one of those counts is a COUNT
  // OVER A PRISM CACHE, so each is 0 both when AWS reported nothing and when the
  // read feeding that cache has never completed. The server stamps one timestamp
  // per cache for exactly this — and until now nobody read three of the five.
  // "AWS's last sweep found no address space" may only be said once all three
  // caches behind those counts have been read whole.
  const inventoryCachesRead = !!(config?.scopeCidrsReadAt && config?.discoveredCidrsReadAt
    && config?.publicAddressesReadAt);
  const inventoryEmpty = inventoryLooksEmpty && inventoryCachesRead;
  const inventoryUnread = inventoryLooksEmpty && !inventoryCachesRead;

  // The Public IPs tile's own cache. A zero here reads as "you have no public
  // IPs", which is a bill-shaped claim nothing has established when the
  // public-address read has never landed.
  const publicAddressesRead = !!config?.publicAddressesReadAt;

  const noteSeverity = ipamConfigNoteSeverity(config?.note, config?.noteSource);
  const noteTitle = ipamConfigNoteTitle(config?.note, config?.noteSource);

  // "of 0 VPCs" is a denominator, and a denominator of zero on a posture tile
  // reads as "we checked everything and it was fine". When the scope view has
  // reported nothing there is no denominator at all, and the caption says which
  // of the two it is.
  // THE EMPTY CARD, PICKED IN ONE PLACE. Two states, and the honest zero is only
  // the second — same IIFE shape as postureChipLabel below, so the three-way
  // choice reads as a choice rather than as a nested ternary in the JSX.
  const inventoryEmptyCard = (() => {
    if (inventoryUnread) {
      return {
        title: IPAM_DASHBOARD_INVENTORY_UNREAD_TITLE,
        description: IPAM_DASHBOARD_INVENTORY_UNREAD_BODY,
      };
    }
    if (inventoryEmpty) {
      return {
        title: "AWS's last sweep found no address space",
        description: `AWS discovered no VPCs, subnets, EIPs or public IPv4 pools in ${scopePhrase}`
          + `${lastSweepAbs ? `, as of ${lastSweepAbs}` : ''}.`,
      };
    }
    return null;
  })();

  const postureDenominator = (() => {
    if (scopeViewBehind) return 'Not assessed — no VPC in the scope view yet';
    if (vpcCount === null) return 'VPC rows only';
    if (someVpcsUnassessed) {
      return `of ${formatCount(vpcCount)} assessed — at least `
        + `${formatCount(unassessedVpcFloor)} more not yet assessed`;
    }
    return `of ${formatCount(vpcCount)} VPCs — VPC rows only`;
  })();

  const header = (
    <PortalPageHeader
      icon={<LanIcon sx={{ fontSize: 24 }} />}
      title="VPC IPAM"
      color={IPAM_COLOR}
      subtitle={(
        <>
          Host account <b>{config?.hostAccountId || '—'}</b>
          {' • '}Home Region <b>{config?.homeRegion || '—'}</b>
          {' • '}Last discovery <b>{lastSweepRel || 'never'}</b>
          {config?.tier && (
            <>
              {' '}
              <Chip
                size="small"
                label={tierChip.label}
                sx={{ ...statusChipSx(tierChip.intent), ml: 0.5 }}
              />
            </>
          )}
        </>
      )}
      action={(
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
          <Tooltip title={refreshing ? 'A refresh is already running' : 'Re-read discovered address space, public IPs and pools from AWS'}>
            <span>
              <Button
                variant="contained"
                size="small"
                startIcon={<RefreshIcon />}
                disabled={refreshing || starting}
                onClick={() => onRefresh('all')}
                sx={ipamContainedSx}
              >
                {refreshing ? 'Refreshing…' : 'Refresh'}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      )}
    />
  );

  if (loading && !config) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{header}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading VPC IPAM…" color={IPAM_COLOR} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }
  if (error && !config) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{header}</motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={() => loadAll().catch((e) => setError(e.message))} />
        </motion.div>
      </motion.div>
    );
  }
  if (!config) return null;

  // --- The metered-scope panel. Rendered in EVERY state, cold start included:
  //     the meter starts when the IPAM is created, not when the first sweep
  //     lands, so hiding the cost for the first twenty minutes would hide it
  //     during the only window in which the customer can still cheaply reverse
  //     the decision.
  const meteredPanel = (
    <Paper elevation={0} sx={sectionCardSx}>
      <PortalSectionHeader
        icon={<PaidOutlinedIcon sx={{ fontSize: 20 }} />}
        title="Metered scope"
        subtitle={IPAM_METERED_SUBTITLE}
        color={IPAM_COLOR}
      />
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box sx={insetPanelSx}>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.8rem', color: CK.body, mb: 0.5 }}>
              {IPAM_ACTIVE_IPS_TILE_LABEL}
            </Typography>
            <Typography
              sx={{
                fontFamily: FONT_BODY,
                fontWeight: 700,
                fontSize: '2rem',
                color: activeIps === null ? CK.muted : CK.navy,
                lineHeight: 1.1,
              }}
            >
              {activeIps === null ? '—' : formatCount(activeIps)}
            </Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box sx={insetPanelSx}>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.8rem', color: CK.body, mb: 0.5 }}>
              Estimated monthly cost
            </Typography>
            <Stack direction="row" spacing={1} alignItems="baseline" useFlexGap flexWrap="wrap">
              {/* THE FIGURE IS KEPT, NOT HIDDEN — it is the last-known bill and
                  hiding it would answer a money question with silence — but it
                  stops being rendered as money accruing right now the moment the
                  ledger says the meter has stopped. */}
              <Typography
                sx={{
                  fontFamily: FONT_BODY,
                  fontWeight: 700,
                  fontSize: '2rem',
                  color: monthlyCost === null || meterStopped ? CK.muted : CK.navy,
                  lineHeight: 1.1,
                }}
              >
                {monthlyCost === null ? '—' : monthlyCost}
              </Typography>
              <Chip size="small" label={costChip.label} sx={statusChipSx(costChip.intent)} />
            </Stack>
          </Box>
        </Grid>
      </Grid>
      {/* THE PROVENANCE, AND IT WAS WRONG. This caption said the figure was
          "counted from the address space AWS has discovered so far" — the
          provenance of the DISCOVERED CIDR tile a few centimetres above it. The
          meter is DescribeNetworkInterfaces: attached ENIs, which is what AWS
          bills on. Two different numbers with two different meanings on one
          screen, and the caption pointed each at the other. */}
      <Typography sx={{ ...captionSx, mt: 2 }}>
        {ipamMeteredCaption({
          activeIps,
          price: IPAM_ADVANCED_PRICE,
          countedRel: activeIpsCountedRel,
        })}
      </Typography>
      {/* THE LEDGER'S VERDICT ON THE METER, IN THE ONE SPELLING THE DANGER ZONE
          AND THE RESIDUE BANNER ALSO USE. Three arms including the undefined one,
          which reads "treat it as billing until the ledger says otherwise" — the
          only safe direction for a claim about money that nothing established. */}
      <Typography sx={{ ...captionSx, mt: 1 }}>
        {/* The verified flag travels with the verdict it qualifies. The chips above
            already hedge on it, and a caption that went on asserting "still billing
            right now" beneath a chip reading "not verified" would be the two halves
            of one tile disagreeing about the same meter. Neither says HOW the
            verdict was reached: `verified === false` covers a sighting Prism has
            stopped refreshing and a ledger that never held one, and only
            `billingNote` below knows which. */}
        {ipamPreviewBillingLine(stillBilling, summary?.billingVerified)}
      </Typography>
      {/* The backend's own reason, when it sent one. It knows why it answered
          what it answered and this page does not. */}
      {billingNote && <Typography sx={{ ...captionSx, mt: 1 }}>{billingNote}</Typography>}
      {stillBilling === true && ipamAdopted && (
        <Typography sx={{ ...captionSx, mt: 1, fontWeight: 700 }}>
          {`Prism never deletes an IPAM it did not create, so no button here can stop this meter. ${ipamDeleteItYourself(config)}`}
        </Typography>
      )}
      <Typography sx={{ ...captionSx, mt: 1 }}>{IPAM_PRICE_NOTE}</Typography>
    </Paper>
  );

  // --- Discovery health. Also rendered in every state: it is where "we cannot
  //     tell" lives, and that answer is most needed exactly when the tables
  //     above look clean.
  const discoveryPanel = (
    <Paper elevation={0} sx={sectionCardSx}>
      <PortalSectionHeader
        icon={<TravelExploreIcon sx={{ fontSize: 20 }} />}
        title="Discovery health"
        subtitle="Whether AWS is still inventorying every enrolled account"
        color={IPAM_COLOR}
        action={(
          <Button
            size="small"
            startIcon={<AccountTreeIcon />}
            onClick={() => navigate('/securityhub/ipam/accounts')}
            sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none', color: IPAM_COLOR }}
          >
            Accounts page
          </Button>
        )}
      />
      {!discovery.known ? (
        <Alert severity="warning" sx={{ fontFamily: FONT_BODY }}>
          Prism could not read enrollment health{membersError ? `: ${membersError}` : '.'}
          {' '}
          This page therefore cannot confirm that every enrolled account is still being
          discovered — treat the counts above as covering an unknown set of accounts.
        </Alert>
      ) : (
        <>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Chip
              size="small"
              label={`Discovering: ${discovery.discovering === null ? '—' : discovery.discovering}`}
              sx={statusChipSx('success')}
            />
            <Chip
              size="small"
              label={`Not discovered: ${discovery.notFound}`}
              sx={statusChipSx(discovery.notFound > 0 ? 'error' : 'neutral')}
            />
            <Chip
              size="small"
              label={`Operating Regions: ${regionCount === null ? '—' : regionCount}`}
              sx={statusChipSx('info')}
            />
          </Stack>
          <Typography sx={{ ...captionSx, mt: 2 }}>
            AWS discovery is a periodic background sweep, not a live query. Last sweep recorded
            by Prism: <b>{lastSweepAbs || 'never'}</b>.
          </Typography>
        </>
      )}
    </Paper>
  );

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>{header}</motion.div>

      {/* === STATE C — silent breakage. ALWAYS FIRST, above every number on the
          page (the OrphanScanPage.js:573-600 rule). A coverage gap discovered
          after the operator has read a clean table is a coverage gap that was
          never communicated. === */}
      {brokenDiscovery && (
        <motion.div variants={sectionVariants}>
          <Alert
            severity="warning"
            sx={{ mb: 3, fontFamily: FONT_BODY }}
            action={(
              <Button
                size="small"
                onClick={() => navigate('/securityhub/ipam/accounts')}
                sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' }}
              >
                Review on the Accounts page
              </Button>
            )}
          >
            <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
              {discovery.notFound} of {discovery.inScope} accounts are no longer being discovered.
            </AlertTitle>
            Their resource discovery was deleted or their RAM share was revoked, so anything in
            those accounts is missing from every table on this page, however clean it looks.
            {discovery.notFoundAccounts?.length > 0 && (
              <Box component="span" sx={{ display: 'block', mt: 0.5, fontFamily: FONT_MONO, fontSize: '0.8rem' }}>
                {discovery.notFoundAccounts.join(', ')}
              </Box>
            )}
          </Alert>
        </motion.div>
      )}

      {/* The ENABLE partial-failure alert, and only that. When the caller has
          handed down a non-empty residue map the same `partial_failure` status is
          a half-failed TEARDOWN — named accounts, resources still standing, a
          meter still running — which IpamEntry has already said in stronger words
          directly above this component. Saying it again here in weaker, wrong
          words is not redundancy, it is a contradiction. */}
      {config.status === 'partial_failure' && !residuePresent(residue) && (
        <motion.div variants={sectionVariants}>
          <Alert
            severity="warning"
            sx={{ mb: 3, fontFamily: FONT_BODY }}
            action={(
              <Button
                size="small"
                onClick={() => navigate('/securityhub/ipam/accounts')}
                sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' }}
              >
                Review
              </Button>
            )}
          >
            VPC IPAM enrolled some accounts and not others. The IPAM is running and billing.
            {config.errorMessage ? ` ${config.errorMessage}` : ''}
          </Alert>
        </motion.div>
      )}

      {/* `config.note` IS ONE SLOT WITH SEVERAL WRITERS, AND ONLY TWO OF THEM
          ARE INFORMATION. models/ipam_models.go keeps NoteSource for exactly this
          reason: the estimate's and the consent record's notes are statements
          about a figure, and the UNATTRIBUTED ones are the DEGRADATION channel —
          setConfigNote clears note_source on purpose. The loudest of those is
          applyIpamDrift's "AWS no longer reports IPAM %s … Nothing is being
          discovered and no pool can be allocated from; disable and re-enable",
          which is not informational by any reading, and which was drawn in the
          quietest severity MUI has. */}
      {noteSeverity && (
        <motion.div variants={sectionVariants}>
          <Alert severity={noteSeverity} sx={{ mb: 3, fontFamily: FONT_BODY }}>
            {noteTitle && (
              <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>{noteTitle}</AlertTitle>
            )}
            {config.note}
          </Alert>
        </motion.div>
      )}

      {refreshError && (
        <motion.div variants={sectionVariants}>
          <Alert severity="error" sx={{ mb: 3, fontFamily: FONT_BODY }} onClose={() => setRefreshError(null)}>
            Could not start a refresh: {refreshError}
          </Alert>
        </motion.div>
      )}

      {degraded && (
        <motion.div variants={sectionVariants}>
          <Alert severity="warning" sx={{ mb: 3, fontFamily: FONT_BODY }}>
            The last refresh completed with gaps
            {(job?.unitsFailed || 0) > 0 ? ` — ${job.unitsFailed} unit(s) failed` : ''}
            {(job?.unitsUnscanned || 0) > 0 ? ` — ${job.unitsUnscanned} unit(s) unscanned` : ''}
            {job?.note ? `. ${job.note}` : '.'}
            {' '}Counts below cover less than the full enrollment.
          </Alert>
        </motion.div>
      )}

      {refreshing && (
        <motion.div variants={sectionVariants}>
          <OperationProgress
            percent={job?.progress}
            label={job?.progressLabel || 'Refreshing VPC IPAM inventory…'}
            sx={{ mb: 2 }}
          />
        </motion.div>
      )}

      {/* Rendered in the cold state too. A rollup endpoint that is failing is
          real breakage, and the cold-start panel's "nothing is wrong" is a
          statement about AWS's sweep, not about Prism's ability to read it. */}
      {summaryError && (
        <motion.div variants={sectionVariants}>
          <Alert severity="error" sx={{ mb: 3, fontFamily: FONT_BODY }}>
            <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
              Address-space rollups are unavailable
            </AlertTitle>
            {summaryError}. Every count on this page is shown as an em-dash rather than a zero:
            Prism does not know these numbers, which is not the same as knowing they are zero.
          </Alert>
        </motion.div>
      )}

      {/* A background poll that gave up three times running is not a quiet
          condition — the page is now showing a snapshot that has stopped
          updating itself. */}
      {error && (
        <motion.div variants={sectionVariants}>
          <Alert severity="warning" sx={{ mb: 3, fontFamily: FONT_BODY }} onClose={() => setError(null)}>
            Prism stopped refreshing this page automatically: {error}. What you see is the last
            reading, not the current one — use Refresh to try again.
          </Alert>
        </motion.div>
      )}

      {/* === STATE A — cold start. Server-discriminated on firstDiscoveryAt. === */}
      {coldStart ? (
        <>
          <motion.div variants={sectionVariants}>
            <Paper elevation={0} sx={sectionCardSx}>
              <IpamDiscoveryColdStart
                associations={discovery.associations}
                enabledAt={config.costAcknowledgedAt || config.createdAt}
                lastCheckedAt={lastCheckedAt}
                // The SERVER's last completed sweep. Without it the panel cannot
                // tell "AWS has nothing yet" from "Prism has never looked", and
                // states the first as fact in both cases.
                lastSweepAt={config.lastDiscoveryRefreshAt}
                onCheck={() => onRefresh('discovery')}
                checking={starting || refreshing}
              />
            </Paper>
          </motion.div>
          <motion.div variants={sectionVariants} style={{ marginTop: 24 }}>{meteredPanel}</motion.div>
          <motion.div variants={sectionVariants} style={{ marginTop: 24 }}>{discoveryPanel}</motion.div>
        </>
      ) : (
        <>
          {/* No motion wrapper on the tile row: the tiles animate themselves in
              pure CSS, and tileEntranceStyle(i) goes on a BARE <div> inside the
              <Grid> — a motion variant nested in MUI Grid never receives the
              parent's animate label and leaves correctly-sized invisible tiles,
              which shipped twice. */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <div style={tileEntranceStyle(0)}>
                {/* BOUND TO THE TABLE ITS OWN LABEL AND HINT DESCRIBE. This tile
                    read resourceCidrCount — ipam_scope_cidrs — while calling
                    itself "Discovered CIDRs" and explaining, in its hint, that it
                    counts what the resource discoveries reported. Those are two
                    different AWS views and they are not in step: the tile printed
                    0 over 14 discovered CIDRs on the POC tenant. */}
                <StatTile
                  icon={<LayersIcon sx={{ fontSize: 28 }} />}
                  value={discoveredCidrCount}
                  color={CK.blue}
                  label="Discovered CIDRs"
                  caption={scopeViewBehind
                    ? 'None assessed by the scope view yet'
                    : 'VPCs, subnets and prefixes in scope'}
                  hint={"Every resource CIDR AWS's resource discoveries have reported, across all "
                    + 'resource types. The posture tiles beside it come from a second, later AWS '
                    + 'view — the IPAM scope — which has reported '
                    + (resourceCidrCount === null
                      ? 'a number Prism could not read. '
                      : `${formatCount(resourceCidrCount)} of them so far. `)
                    + IPAM_ADDRESS_SPACE_VS_METER}
                  onClick={() => navigate('/securityhub/ipam/address-space')}
                />
              </div>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <div style={tileEntranceStyle(1)}>
                <StatTile
                  icon={<WarningAmberIcon sx={{ fontSize: 28 }} />}
                  value={scopeViewBehind ? null : overlapping}
                  color={CK.red}
                  label="Overlapping VPCs"
                  caption={postureDenominator}
                  hint={scopeViewBehind
                    ? 'AWS has not reported a single VPC in the IPAM scope view yet, so nothing has assessed anything for overlap. The tile shows unknown rather than 0 — a zero here would read as "no address-space conflicts anywhere in your organization".'
                    : "AWS reports overlap only on vpc rows; subnets carry no overlap status at all, so this is scoped to VPCs and not to the whole inventory."}
                  onClick={() => navigate('/securityhub/ipam/address-space')}
                />
              </div>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <div style={tileEntranceStyle(2)}>
                <StatTile
                  icon={<RuleIcon sx={{ fontSize: 28 }} />}
                  value={scopeViewBehind ? null : noncompliant}
                  color={CK.amber}
                  label="Noncompliant VPCs"
                  caption={postureDenominator}
                  hint={scopeViewBehind
                    ? 'AWS has not reported a single VPC in the IPAM scope view yet, so no CIDR has been judged against any pool rule. The tile shows unknown rather than 0.'
                    : "Compliance is AWS's judgement of a VPC's CIDR against its IPAM pool's allocation rules. It is populated on vpc rows only."}
                  onClick={() => navigate('/securityhub/ipam/address-space')}
                />
              </div>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <div style={tileEntranceStyle(3)}>
                <StatTile
                  icon={<PublicIcon sx={{ fontSize: 28 }} />}
                  value={publicAddressesRead ? publicAddressCount : null}
                  color={CK.purple}
                  label="Public IPs"
                  caption="Elastic IPs and public IPv4 addresses"
                  unavailableCaption={publicAddressesRead
                    ? undefined
                    : IPAM_PUBLIC_IPS_TILE_UNREAD_CAPTION}
                  hint={publicAddressesRead
                    ? 'From Public IP Insights — every public IPv4 address discovery can see, attached or idle.'
                    : IPAM_PUBLIC_IPS_TILE_UNREAD_HINT}
                  onClick={() => navigate('/securityhub/ipam/public-ips')}
                />
              </div>
            </Grid>
          </Grid>

          {/* === THE SCOPE VIEW IS BEHIND DISCOVERY. Not an error, not a zero,
              and not a state this page used to have. Every posture number below
              is scoped to ipam_scope_cidrs, AWS has put nothing in it yet, and
              the tiles above now say "unknown" instead of "0" — which needs
              explaining once, at the top, rather than three times in tooltips. === */}
          {!scopeViewBehind && someVpcsUnassessed && (
            <motion.div variants={sectionVariants}>
              <Alert severity="info" sx={{ mb: 3, fontFamily: FONT_BODY }}>
                <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
                  At least {formatCount(unassessedVpcFloor)} discovered{' '}
                  {unassessedVpcFloor === 1 ? 'VPC is' : 'VPCs are'} not in the IPAM scope view yet
                </AlertTitle>
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem' }}>
                  AWS&apos;s resource discoveries have reported {formatCount(discoveredVpcCount)}{' '}
                  VPCs and its per-scope view — the source of every compliance, overlap and
                  management figure below — has reported {formatCount(vpcCount)} of them. The
                  posture numbers on this page are denominated in the assessed ones only, so the
                  remainder is neither compliant nor non-compliant here: it is unassessed. This is
                  the ordinary state for a few minutes after an account is enrolled.
                </Typography>
              </Alert>
            </motion.div>
          )}

          {scopeViewBehind && (
            <motion.div variants={sectionVariants}>
              <Alert severity="info" sx={{ mb: 3, fontFamily: FONT_BODY }}>
                <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
                  AWS has not populated the IPAM scope view yet
                </AlertTitle>
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem' }}>
                  AWS&apos;s resource discoveries have reported{' '}
                  {formatCount(discoveredCidrCount)} CIDRs, and they are listed in full on the{' '}
                  <b>Address space</b> page. Compliance, overlap and management state come from a
                  second, later view — the IPAM scope — which AWS populates after a discovery sweep
                  lands and which is still empty for this IPAM. Every posture figure on this page
                  therefore reads as unknown rather than zero: nothing has assessed this address
                  space yet, which is not the same as it being clean. It normally clears within a
                  few minutes of the first sweep.
                </Typography>
              </Alert>
            </motion.div>
          )}

          {/* === STATE B — genuinely empty, and SCOPED. The bare words "No
              resources found" are never rendered: an honest zero that does not
              name what it covers is indistinguishable from a broken pipeline. === */}
          {inventoryEmptyCard ? (
            <motion.div variants={sectionVariants}>
              <Paper elevation={0} sx={sectionCardSx}>
                <PortalEmptyState
                  icon={<LayersIcon sx={{ fontSize: 28 }} />}
                  title={inventoryEmptyCard.title}
                  description={inventoryEmptyCard.description}
                  color={IPAM_COLOR}
                />
              </Paper>
            </motion.div>
          ) : (
            <>
              {/* === Highest utilization === */}
              <motion.div variants={sectionVariants}>
                <Paper elevation={0} sx={sectionCardSx}>
                  <PortalSectionHeader
                    icon={<SpeedIcon sx={{ fontSize: 20 }} />}
                    title="Highest utilization"
                    subtitle="The address space closest to exhaustion"
                    color={IPAM_COLOR}
                  />
                  {topUtilization.length === 0 ? (
                    <Typography sx={captionSx}>
                      {/* eslint-disable-next-line no-nested-ternary */}
                      {summaryError
                        ? 'Not available — Prism could not read this rollup.'
                        : (scopeViewBehind
                          // The ranking reads ipam_scope_cidrs.ip_usage. With that
                          // table empty, "AWS reported no computable IP usage" is a
                          // claim about a question AWS has not been asked yet.
                          ? 'Not ranked yet — this ranking reads the IPAM scope view, which AWS has not '
                            + 'populated for this IPAM. The discovered CIDRs carry real utilization and are '
                            + 'listed on the Address space page.'
                          : `AWS reported no computable IP usage in ${scopePhrase}`
                            + `${lastSweepAbs ? `, as of ${lastSweepAbs}` : ''}.`)}
                    </Typography>
                  ) : (
                    <TableContainer sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={tableHeadRowSx}>
                            <TableCell sx={headCellSx}>Resource</TableCell>
                            <TableCell sx={headCellSx}>CIDR</TableCell>
                            <TableCell sx={headCellSx}>Account</TableCell>
                            <TableCell sx={headCellSx}>Region</TableCell>
                            <TableCell sx={{ ...headCellSx, width: 200 }}>Utilization</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {topUtilization.map((row, index) => (
                            <TableRow
                              key={`${row.resourceId}:${row.resourceCidr}`}
                              hover
                              sx={{ ...rowSx(index), cursor: 'pointer' }}
                              onClick={() => navigate('/securityhub/ipam/address-space')}
                            >
                              <TableCell sx={bodyCellSx}>
                                {row.resourceName || row.resourceId || '—'}
                                {row.resourceType && (
                                  <Chip
                                    size="small"
                                    label={row.resourceType}
                                    sx={{ ...statusChipSx('neutral'), ml: 1 }}
                                  />
                                )}
                              </TableCell>
                              <TableCell sx={cidrCellSx}>{row.resourceCidr || '—'}</TableCell>
                              <TableCell sx={cidrCellSx}>{row.resourceOwnerId || '—'}</TableCell>
                              <TableCell sx={bodyCellSx}>{row.resourceRegion || '—'}</TableCell>
                              <TableCell sx={bodyCellSx}>
                                {/* THRESHOLDS ARE PASSED, NEVER INHERITED. With no
                                    prop UtilizationBar falls back to the portal-wide
                                    UTILIZATION_THRESHOLDS {70, 90} while Address Space
                                    judges by {80, 95} — and this row navigates to
                                    Address Space, so a VPC at 75% went amber here and
                                    green one click later. One constant, imported by
                                    both (ipamState.js). */}
                                <UtilizationBar
                                  value={usagePercent(row.ipUsage)}
                                  thresholds={IPAM_UTILIZATION_THRESHOLDS}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {/* BOTH DENOMINATORS COUNT ipam_scope_cidrs.ip_usage, so when the
                      scope view is empty they are well-formed zeros about a
                      question AWS has not been asked — "across the 0 CIDRs AWS
                      could measure" and "0 CIDRs are left out on that basis",
                      printed directly beneath the sentence admitting the view is
                      unpopulated. The caption is the same class of confident
                      negative as the table above it and takes the same branch. */}
                  {scopeViewBehind ? (
                    <Typography sx={{ ...captionSx, mt: 2 }}>
                      Neither denominator can be quoted yet: both count the IPAM scope view, which
                      AWS has not populated. The discovered CIDRs do carry real utilization — see
                      the Address space page — but nothing here has measured or excluded any of
                      them.
                    </Typography>
                  ) : (
                    <Typography sx={{ ...captionSx, mt: 2 }}>
                      Ranked by AWS&apos;s reported IP usage
                      {measuredCidrs === null
                        ? ''
                        : ` across the ${formatCount(measuredCidrs)} `
                          + `${measuredCidrs === 1 ? 'CIDR' : 'CIDRs'} AWS could measure`}.
                      {' '}
                      Prefixes AWS cannot compute utilization for — every IPv6 prefix is one — are
                      excluded from this ranking rather than counted as 0% used, which would read as
                      an empty, healthy space.
                      {' '}
                      {unmeasurableCidrs === null
                        ? 'Prism could not read how much of the estate that leaves out.'
                        : `${formatCount(unmeasurableCidrs)} `
                          + `${unmeasurableCidrs === 1 ? 'CIDR is' : 'CIDRs are'} left out on that `
                          + `basis, so this ranking says nothing about `
                          + `${unmeasurableCidrs === 1 ? 'it' : 'them'}.`}
                    </Typography>
                  )}
                </Paper>
              </motion.div>

              {/* === VPC-scoped posture === */}
              <motion.div variants={sectionVariants} style={{ marginTop: 24 }}>
                <Paper elevation={0} sx={sectionCardSx}>
                  <PortalSectionHeader
                    icon={<FactCheckOutlinedIcon sx={{ fontSize: 20 }} />}
                    title="Posture — VPCs only"
                    subtitle="Compliance and overlap as AWS reports them"
                    color={IPAM_COLOR}
                    action={(
                      <Chip
                        size="small"
                        // eslint-disable-next-line no-nested-ternary
                        label={postureChipLabel}
                        sx={statusChipSx(scopeViewBehind || someVpcsUnassessed ? 'warning' : 'info')}
                      />
                    )}
                  />
                  {/* The caption is not decoration. AWS populates compliance,
                      overlap and management state on vpc rows ONLY — in the
                      spike's own sample 12 of 15 rows (every subnet) left all
                      three blank while still carrying a real IpUsage. A
                      percentage over the whole inventory would be arithmetic
                      over a column that exists for some rows and not others. */}
                  {scopeViewBehind ? (
                    <Typography sx={{ ...captionSx, mb: 2 }}>
                      These five verdicts come from AWS&apos;s IPAM scope view, and AWS has not
                      reported a single VPC in it yet. Nothing below has been assessed — the
                      buckets are not zero, they are absent.
                    </Typography>
                  ) : (
                    <Typography sx={{ ...captionSx, mb: 2 }}>
                      AWS reports compliance and overlap on <b>VPC rows only</b>. Subnets carry real
                      utilization but no posture at all, so these counts are scoped to
                      {vpcCount === null ? ' the VPCs AWS has assessed' : ` the ${formatCount(vpcCount)} VPCs AWS has assessed`}
                      {resourceCidrCount === null ? '' : `, not to all ${formatCount(resourceCidrCount)} CIDRs in the scope view`}
                      {someVpcsUnassessed ? `, and at least ${formatCount(unassessedVpcFloor)} discovered VPCs are not in that view yet` : ''}.
                    </Typography>
                  )}
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box sx={insetPanelSx}>
                        <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: '0.875rem', color: CK.navy, mb: 1.5 }}>
                          Compliance
                        </Typography>
                        <PostureCounts
                          total={vpcCount}
                          unassessed={scopeViewBehind}
                          unassessedFloor={unassessedVpcFloor}
                          buckets={[
                            {
                              key: 'compliant',
                              label: 'Compliant',
                              intent: 'success',
                              hint: 'VPCs whose CIDR sits inside space provisioned to an IPAM pool and matches that pool’s allocation rules.',
                              value: countOrNull(summary?.compliance?.compliant),
                            },
                            {
                              key: 'noncompliant',
                              label: 'Noncompliant',
                              intent: 'error',
                              hint: 'VPCs allocated from an IPAM pool whose CIDR no longer matches that pool’s rules.',
                              value: countOrNull(summary?.compliance?.noncompliant),
                            },
                            {
                              key: 'unmanaged',
                              label: 'Unmanaged',
                              intent: 'warning',
                              hint: 'VPCs no IPAM pool governs — real address space that nothing is planning around.',
                              value: countOrNull(summary?.compliance?.unmanaged),
                            },
                            {
                              key: 'ignored',
                              label: 'Ignored',
                              intent: 'neutral',
                              hint: 'VPCs excluded from IPAM monitoring. IPAM makes no claim about them either way.',
                              value: countOrNull(summary?.compliance?.ignored),
                            },
                            notReportedBucket('compliance', countOrNull(summary?.compliance?.unrecognized)),
                          ]}
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box sx={insetPanelSx}>
                        <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: '0.875rem', color: CK.navy, mb: 1.5 }}>
                          Overlap
                        </Typography>
                        <PostureCounts
                          total={vpcCount}
                          unassessed={scopeViewBehind}
                          unassessedFloor={unassessedVpcFloor}
                          buckets={[
                            {
                              key: 'overlapping',
                              label: 'Overlapping',
                              intent: 'error',
                              hint: 'VPCs whose CIDR collides with another CIDR in the same scope — often one in a different account. Two resources hold addresses that cannot both be routed.',
                              value: countOrNull(summary?.overlap?.overlapping),
                            },
                            {
                              key: 'nonoverlapping',
                              label: 'No overlap',
                              intent: 'success',
                              hint: 'VPCs no other CIDR in the scope collides with.',
                              value: countOrNull(summary?.overlap?.nonoverlapping),
                            },
                            {
                              key: 'ignored',
                              label: 'Ignored',
                              intent: 'neutral',
                              hint: 'VPCs excluded from overlap monitoring.',
                              value: countOrNull(summary?.overlap?.ignored),
                            },
                            notReportedBucket('overlap', countOrNull(summary?.overlap?.unrecognized)),
                          ]}
                        />
                      </Box>
                    </Grid>
                  </Grid>

                  {/* MANAGED / UNMANAGED: rendered ONLY when the server has
                      already separated the converged rows from the pending ones.
                      ManagementState is an eventually-consistent observation with
                      a multi-minute lag — measured absent -> managed in ~7 min
                      same-account and ~12 min cross-account — so a fresh
                      resource reading 'unmanaged' is PENDING, not non-compliant.
                      Without a server-side `pending` bucket there is no honest
                      way to draw that line from a rollup (the gate needs each
                      row's CachedAt), so the space is left empty rather than
                      filled with a number that counts converging resources as
                      findings. */}
                  {countOrNull(summary?.management?.pending) !== null ? (
                    <Box sx={{ mt: 3 }}>
                      <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: '0.875rem', color: CK.navy, mb: 1.5 }}>
                        Management state
                      </Typography>
                      <PostureCounts
                        total={vpcCount}
                        unassessed={scopeViewBehind}
                        unassessedFloor={unassessedVpcFloor}
                        buckets={[
                          {
                            key: 'managed',
                            label: 'Managed',
                            intent: 'success',
                            hint: 'VPCs whose CIDR IPAM is managing.',
                            value: countOrNull(summary?.management?.managed),
                          },
                          {
                            key: 'unmanaged',
                            label: 'Unmanaged',
                            intent: 'warning',
                            hint: 'VPCs IPAM knows about but whose CIDR it does not manage — and which Prism has watched long enough to be sure that is settled, not pending.',
                            value: countOrNull(summary?.management?.unmanaged),
                          },
                          {
                            key: 'pending',
                            label: 'Still settling',
                            intent: 'info',
                            hint: 'Unmanaged VPCs Prism first saw too recently to judge. AWS converges management state minutes after a resource is created, so these are pending rather than non-compliant.',
                            value: countOrNull(summary?.management?.pending),
                          },
                          {
                            key: 'ignored',
                            label: 'Ignored',
                            intent: 'neutral',
                            hint: 'VPCs excluded from IPAM management.',
                            value: countOrNull(summary?.management?.ignored),
                          },
                          notReportedBucket('management state', countOrNull(summary?.management?.unrecognized)),
                        ]}
                      />
                      <Typography sx={{ ...captionSx, mt: 1.5 }}>
                        AWS converges management state a few minutes after a resource is created —
                        about 7 minutes in-account and about 12 cross-account. Resources inside that
                        window are counted as still settling, never as unmanaged.
                      </Typography>
                    </Box>
                  ) : (
                    <Typography sx={{ ...captionSx, mt: 3 }}>
                      Managed vs unmanaged is not summarised here: AWS converges that value a few
                      minutes after a resource is created — about 7 minutes in-account and about 12
                      cross-account — so a freshly created VPC reads <i>unmanaged</i> while it is
                      merely pending, and a rollup cannot tell the two apart. Per-resource
                      management state is on the Address Space page.
                    </Typography>
                  )}
                </Paper>
              </motion.div>
            </>
          )}

          <motion.div variants={sectionVariants} style={{ marginTop: 24 }}>{meteredPanel}</motion.div>
          <motion.div variants={sectionVariants} style={{ marginTop: 24 }}>{discoveryPanel}</motion.div>
        </>
      )}
    </motion.div>
  );
}
